import {
  EVOLUTIONS,
  SYNERGIES,
  UPGRADES,
  WAVES,
  getCore,
  getEnemy,
  getUpgrade,
  validateContent,
} from '../../data/content';
import { TypedEventBus } from './EventBus';
import {
  applyShopPurchase,
  createCoreStats,
  eligibleEvolution,
  generateShopOffers,
  generateUpgradeOffers,
  mitigateDamage,
  newlyUnlockedSynergies,
  recalculateCoreStats,
  xpRequiredForLevel,
} from './Progression';
import { SeededRng } from './Rng';
import { SpatialHash } from './SpatialHash';
import { ObjectPool } from './ObjectPool';
import {
  CORE_X,
  CORE_Y,
  FIXED_STEP,
  type EliteModifier,
  type EnemyKind,
  type EnemyState,
  type EvolutionDefinition,
  type GameEvent,
  type GameEventMap,
  type MetaModifiers,
  type ProjectileState,
  type RunPhase,
  type RunState,
  type ShopUpgradeId,
  type SimulationCommand,
  type SimulationSnapshot,
  type UpgradeOffer,
  type Vec2,
  type WeaponFamily,
  type XpPickupState,
  type XpTier,
} from './types';

const CORE_RADIUS = 27;
const PLAYER_PROJECTILE_SPEED = 330;
const ENEMY_PROJECTILE_SPEED = 115;
const MAX_ENEMIES = 360;
const MAX_PROJECTILES = 560;
const MAX_XP_PICKUPS = 420;
const PLAYER_SPEED = 150;

export type SimulationMode = 'standard' | 'endgame-test';

export function calculatePursuitSpeed(
  baseSpeed: number,
  playerSpeed: number,
  distance: number,
  huntProtocol: boolean,
): number {
  if (huntProtocol) {
    const huntMultiplier = 1.35 + Math.min(0.55, distance / 700);
    return Math.max(baseSpeed, playerSpeed * huntMultiplier);
  }
  if (distance <= 270) return baseSpeed;
  const catchUpMultiplier = 1.18 + Math.min(0.62, (distance - 270) / 650);
  return Math.max(baseSpeed, playerSpeed * catchUpMultiplier);
}

export function calculateEnemyMovementSpeed(
  pursuitSpeed: number,
  playerSpeed: number,
  slowFactor: number,
  speedMultiplier: number,
  huntProtocol: boolean,
): number {
  const modifiedSpeed = pursuitSpeed * slowFactor * speedMultiplier;
  return huntProtocol ? Math.max(modifiedSpeed, playerSpeed * 1.12) : modifiedSpeed;
}

function pooledEnemy(): EnemyState {
  return {
    id: 0,
    kind: 'basic',
    x: 0,
    y: 0,
    radius: 0,
    health: 0,
    maxHealth: 0,
    shield: 0,
    speed: 0,
    damage: 0,
    rewardCash: 0,
    rewardXP: 0,
    attackCooldown: 0,
    abilityCooldown: 0,
    hitFlash: 0,
    eliteModifier: null,
    status: {
      chillStacks: 0,
      chilledFor: 0,
      frozenFor: 0,
      freezeImmunityFor: 0,
      staticStacks: 0,
      frostbiteFor: 0,
    },
    bossPhase: 0,
    bossShielded: false,
  };
}

function pooledProjectile(): ProjectileState {
  return {
    id: 0,
    source: 'player',
    family: 'kinetic',
    x: 0,
    y: 0,
    previousX: 0,
    previousY: 0,
    velocityX: 0,
    velocityY: 0,
    radius: 0,
    damage: 0,
    ttl: 0,
    pierce: 0,
    bounces: 0,
    explosionRadius: 0,
    hitIds: [],
  };
}

function pooledXpPickup(): XpPickupState {
  return {
    id: 0,
    x: 0,
    y: 0,
    previousX: 0,
    previousY: 0,
    value: 0,
    tier: 'gray',
    radius: 3,
    magnetized: false,
    age: 0,
  };
}

const EMPTY_META: MetaModifiers = {
  powerLevel: 0,
  integrityLevel: 0,
  insightLevel: 0,
  unlockedBlueprints: [],
  selectedCoreId: 'aegis',
  coreMasteryLevel: 0,
};

export class Simulation {
  readonly events = new TypedEventBus();

  private readonly rng: SeededRng;
  private readonly meta: MetaModifiers;
  private readonly spatialHash = new SpatialHash<EnemyState>(64);
  private readonly enemyPool = new ObjectPool<EnemyState>(pooledEnemy, MAX_ENEMIES);
  private readonly projectilePool = new ObjectPool<ProjectileState>(
    pooledProjectile,
    MAX_PROJECTILES,
  );
  private readonly xpPickupPool = new ObjectPool<XpPickupState>(pooledXpPickup, MAX_XP_PICKUPS);
  private readonly eventQueue: GameEvent[] = [];
  private readonly deadEnemyIds = new Set<number>();
  private nextEnemyId = 1;
  private nextProjectileId = 1;
  private nextXpPickupId = 1;
  private gravityCooldown = 0;
  private bossSummonCooldown = 0;
  private bossDisruptionCooldown = 0;
  private teslaVolley = 0;
  private pendingPhaseAfterEvolution: RunPhase = 'combat';
  private state: RunState;

  static start(
    seed: number,
    meta: MetaModifiers = EMPTY_META,
    mode: SimulationMode = 'standard',
  ): Simulation {
    return new Simulation(seed, meta, mode);
  }

  private constructor(seed: number, meta: MetaModifiers, mode: SimulationMode) {
    validateContent();
    this.rng = new SeededRng(seed);
    this.meta = {
      ...meta,
      unlockedBlueprints: [...meta.unlockedBlueprints],
    };
    this.state = this.createInitialState(seed);
    if (mode === 'endgame-test') {
      this.applyEndgameLoadout();
      this.startWave(9);
      this.primeEndgameEncounter();
    } else {
      this.startWave(1);
    }
  }

  step(commands: readonly SimulationCommand[] = []): void {
    for (const command of commands) this.handleCommand(command);
    if (this.state.phase !== 'combat') return;

    this.state.tick += 1;
    this.state.stats.elapsedTime += FIXED_STEP;
    this.state.waveTime += FIXED_STEP;
    this.state.comboTimer = Math.max(0, this.state.comboTimer - FIXED_STEP);
    if (this.state.comboTimer === 0) this.state.combo = 0;
    this.state.overdriveFor = Math.max(0, this.state.overdriveFor - FIXED_STEP);
    this.state.coreAbilityCooldown -= FIXED_STEP;

    if (this.state.core.healthRegen > 0) {
      this.state.core.health = Math.min(
        this.state.core.maxHealth,
        this.state.core.health + this.state.core.healthRegen * FIXED_STEP,
      );
    }

    this.updatePlayer();
    this.updateWaveSpawner();
    this.spatialHash.rebuild(
      this.state.enemies.filter((enemy) => !this.deadEnemyIds.has(enemy.id)),
    );
    this.updateStatusEffects();
    this.updateCoreAbility();
    this.updateEnemies();
    this.updateWeapons();
    this.updateProjectiles();
    this.cleanupEntities();
    this.updateXpPickups();

    if (
      this.state.phase === 'combat' &&
      this.state.waveSpawnFinished &&
      this.state.enemies.length === 0 &&
      this.state.xpPickups.length === 0
    ) {
      this.completeWave();
    }
  }

  getSnapshot(): SimulationSnapshot {
    return { ...this.state, fpsHint: 60 };
  }

  drainEvents(): GameEvent[] {
    return this.eventQueue.splice(0, this.eventQueue.length);
  }

  private createInitialState(seed: number): RunState {
    return {
      seed,
      tick: 0,
      phase: 'combat',
      phaseBeforePause: 'combat',
      wave: 1,
      waveTime: 0,
      spawnCooldown: 0,
      waveSpawnFinished: false,
      level: 1,
      xp: 0,
      xpToNext: xpRequiredForLevel(1),
      pendingLevelUps: 0,
      cash: 0,
      rerollsRemaining: 1,
      evolutionTrigger: false,
      evolutionId: null,
      pendingEvolutionId: null,
      coreId: this.meta.selectedCoreId,
      coreAbilityCooldown: 3.5,
      core: createCoreStats(this.meta),
      player: {
        x: CORE_X,
        y: CORE_Y,
        previousX: CORE_X,
        previousY: CORE_Y,
        velocityX: 0,
        velocityY: 0,
        moveX: 0,
        moveY: 0,
        speed: PLAYER_SPEED,
        distanceTravelled: 0,
      },
      xpPickups: [],
      bossArena: {
        active: false,
        kind: null,
        centerX: CORE_X,
        centerY: CORE_Y,
        radius: 0,
        pulse: 0,
      },
      weapons: [
        { family: 'kinetic', enabled: true, cooldown: 0, visualLevel: 1 },
        { family: 'tesla', enabled: false, cooldown: 0, visualLevel: 0 },
        { family: 'cryo', enabled: false, cooldown: 0, visualLevel: 0 },
      ],
      upgradeRanks: {},
      shopRanks: {
        damage: 0,
        'attack-speed': 0,
        'max-health': 0,
        armor: 0,
        regeneration: 0,
        critical: 0,
      },
      synergies: [],
      enemies: [],
      projectiles: [],
      upgradeOffers: [],
      shopOffers: [],
      resultReward: 0,
      resultProfileXp: 0,
      resultCoreXp: 0,
      blueprintReward: null,
      combo: 0,
      comboTimer: 0,
      momentum: 0,
      overdriveFor: 0,
      stats: { damageDealt: 0, damageTaken: 0, kills: 0, elapsedTime: 0, maxCombo: 0 },
    };
  }

  private applyEndgameLoadout(): void {
    for (const upgrade of UPGRADES) this.state.upgradeRanks[upgrade.id] = upgrade.maxRank;
    this.state.synergies = SYNERGIES.map((synergy) => synergy.id);
    this.state.evolutionId = 'tesla_singularity';
    this.state.pendingEvolutionId = null;
    this.state.evolutionTrigger = false;
    this.state.level =
      1 + UPGRADES.reduce((totalRanks, upgrade) => totalRanks + upgrade.maxRank, 0);
    this.state.xp = 0;
    this.state.xpToNext = xpRequiredForLevel(this.state.level);
    this.state.pendingLevelUps = 0;
    this.state.rerollsRemaining = 0;
    this.state.cash = 9_999;
    for (const shopId of Object.keys(this.state.shopRanks) as ShopUpgradeId[])
      this.state.shopRanks[shopId] = 5;
    this.state.combo = 25;
    this.state.comboTimer = 4;
    this.state.momentum = 100;
    this.state.overdriveFor = 12;
    this.state.coreAbilityCooldown = 0.2;
    recalculateCoreStats(this.state, this.meta);
    this.state.core.health = this.state.core.maxHealth;
    this.state.core.barrier = this.state.core.maxBarrier;
    for (const weapon of this.state.weapons) {
      weapon.enabled = true;
      weapon.cooldown = 0;
      weapon.visualLevel = 4;
    }
  }

  private primeEndgameEncounter(): void {
    const formation: readonly EnemyKind[] = [
      'tank',
      'protector',
      'fast',
      'basic',
      'ranged',
      'fast',
      'tank',
      'basic',
    ];
    for (let index = 0; index < 88; index += 1) {
      const kind = formation[index % formation.length]!;
      const modifier: EliteModifier | null = index % 17 === 0 ? 'armored' : null;
      const enemy = this.spawnEnemy(kind, modifier);
      enemy.maxHealth *= 1.35;
      enemy.health = enemy.maxHealth;
    }
    this.state.waveTime = 24;
    this.state.spawnCooldown = 0.35;
  }

  private handleCommand(command: SimulationCommand): void {
    if (command.type === 'set-movement') {
      const length = Math.hypot(command.x, command.y);
      const scale = length > 1 ? 1 / length : 1;
      this.state.player.moveX = command.x * scale;
      this.state.player.moveY = command.y * scale;
      return;
    }
    if (command.type === 'pause' && this.state.phase !== 'paused') {
      this.state.phaseBeforePause = this.state.phase;
      this.state.phase = 'paused';
      return;
    }
    if (command.type === 'resume' && this.state.phase === 'paused') {
      this.state.phase = this.state.phaseBeforePause;
      return;
    }
    if (command.type === 'select-upgrade' && this.state.phase === 'level-up') {
      this.selectUpgrade(command.upgradeId, command.expectedRank);
      return;
    }
    if (command.type === 'reroll-upgrades' && this.state.phase === 'level-up') {
      if (this.state.rerollsRemaining > 0) {
        this.state.rerollsRemaining -= 1;
        this.state.upgradeOffers = generateUpgradeOffers(this.state, this.meta, this.rng);
      }
      return;
    }
    if (command.type === 'buy-shop' && this.state.phase === 'intermission') {
      if (command.shopId !== null && !applyShopPurchase(this.state, command.shopId)) return;
      recalculateCoreStats(this.state, this.meta);
      this.startWave(this.state.wave + 1);
      return;
    }
    if (command.type === 'acknowledge-evolution' && this.state.phase === 'evolution') {
      this.state.pendingEvolutionId = null;
      if (this.state.pendingLevelUps > 0) this.openLevelUp();
      else this.state.phase = this.pendingPhaseAfterEvolution;
      return;
    }
    if (command.type === 'debug-add-xp') {
      this.grantXp(command.amount);
      return;
    }
    if (command.type === 'debug-damage-core') {
      this.damageCore(command.amount);
      return;
    }
    if (command.type === 'debug-next-wave' && this.state.phase === 'combat') {
      this.state.waveSpawnFinished = true;
      this.state.enemies.forEach((enemy) => this.deadEnemyIds.add(enemy.id));
      this.cleanupEntities();
      this.completeWave();
      return;
    }
    if (command.type === 'debug-force-evolution' && this.state.phase === 'combat') {
      this.state.upgradeRanks.cryo_emitter = 1;
      this.state.upgradeRanks.shatter = 1;
      this.state.upgradeRanks.heavy_rounds = 3;
      this.state.upgradeRanks.deep_freeze = 3;
      if (!this.state.synergies.includes('shatter_rounds'))
        this.state.synergies.push('shatter_rounds');
      this.state.evolutionTrigger = true;
      const evolution = eligibleEvolution(this.state);
      if (evolution) this.triggerEvolution(evolution);
      return;
    }
    if (command.type === 'debug-max-skills') {
      for (const upgrade of UPGRADES) this.state.upgradeRanks[upgrade.id] = upgrade.maxRank;
      this.state.synergies = SYNERGIES.map((synergy) => synergy.id);
      this.state.pendingLevelUps = 0;
      this.state.upgradeOffers = [];
      recalculateCoreStats(this.state, this.meta);
      if (this.state.phase === 'level-up') this.state.phase = 'combat';
      return;
    }
    if (command.type === 'debug-stress' && this.state.phase === 'combat') {
      while (this.state.enemies.length < 300) this.spawnEnemy('basic', null);
      this.spatialHash.rebuild(this.state.enemies);
      const target = this.state.enemies[0];
      if (target) {
        while (this.state.projectiles.length < 500) this.fireKinetic(target);
      }
    }
  }

  private updatePlayer(): void {
    const player = this.state.player;
    player.previousX = player.x;
    player.previousY = player.y;
    const targetVelocityX = player.moveX * player.speed;
    const targetVelocityY = player.moveY * player.speed;
    const response = Math.hypot(player.moveX, player.moveY) > 0.01 ? 0.2 : 0.13;
    player.velocityX += (targetVelocityX - player.velocityX) * response;
    player.velocityY += (targetVelocityY - player.velocityY) * response;
    if (Math.abs(player.velocityX) < 0.02) player.velocityX = 0;
    if (Math.abs(player.velocityY) < 0.02) player.velocityY = 0;

    player.x += player.velocityX * FIXED_STEP;
    player.y += player.velocityY * FIXED_STEP;

    if (this.state.bossArena.active) {
      const arena = this.state.bossArena;
      arena.pulse += FIXED_STEP;
      const deltaX = player.x - arena.centerX;
      const deltaY = player.y - arena.centerY;
      const distance = Math.hypot(deltaX, deltaY);
      const limit = arena.radius - CORE_RADIUS - 8;
      if (distance > limit) {
        player.x = arena.centerX + (deltaX / distance) * limit;
        player.y = arena.centerY + (deltaY / distance) * limit;
        const outwardSpeed =
          player.velocityX * (deltaX / distance) + player.velocityY * (deltaY / distance);
        if (outwardSpeed > 0) {
          player.velocityX -= (deltaX / distance) * outwardSpeed * 1.25;
          player.velocityY -= (deltaY / distance) * outwardSpeed * 1.25;
        }
      }
    }

    player.distanceTravelled += Math.hypot(
      player.x - player.previousX,
      player.y - player.previousY,
    );
  }

  private startWave(waveNumber: number): void {
    const definition = WAVES[waveNumber - 1];
    if (!definition) return;
    this.state.wave = waveNumber;
    this.state.waveTime = 0;
    this.state.spawnCooldown = 0;
    this.state.waveSpawnFinished = definition.boss;
    this.state.shopOffers = [];
    this.state.phase = 'combat';
    this.emit('waveStarted', { wave: waveNumber });

    if (definition.miniBoss) this.activateBossArena('warden', 300);
    if (definition.boss) this.activateBossArena('devourer', 330);

    if (definition.elite) {
      const modifiers: readonly EliteModifier[] = ['armored', 'fast', 'regenerating'];
      this.spawnEnemy(definition.miniBoss ? 'fast' : 'tank', this.rng.pick(modifiers));
    }
    if (definition.miniBoss) this.spawnEnemy(definition.miniBoss, null);
    if (definition.boss) this.spawnEnemy('devourer', null);
  }

  private activateBossArena(kind: 'warden' | 'devourer', radius: number): void {
    const player = this.state.player;
    this.state.bossArena = {
      active: true,
      kind,
      centerX: player.x,
      centerY: player.y,
      radius,
      pulse: 0,
    };
    this.emit('bossArenaLocked', { kind, x: player.x, y: player.y, radius });
  }

  private updateWaveSpawner(): void {
    const wave = WAVES[this.state.wave - 1];
    if (!wave || wave.boss || this.state.waveSpawnFinished) return;
    if (this.state.waveTime >= wave.duration) {
      this.state.waveSpawnFinished = true;
      return;
    }
    if (this.state.enemies.length >= MAX_ENEMIES) return;

    this.state.spawnCooldown -= FIXED_STEP;
    if (this.state.spawnCooldown > 0) return;
    this.state.spawnCooldown = wave.spawnInterval * this.rng.between(0.78, 1.2);

    const totalWeight = wave.enemies.reduce((sum, item) => sum + item.weight, 0);
    let roll = this.rng.next() * totalWeight;
    for (const item of wave.enemies) {
      roll -= item.weight;
      if (roll <= 0) {
        this.spawnEnemy(item.enemy, null);
        break;
      }
    }
  }

  private spawnEnemy(kind: EnemyKind, eliteModifier: EliteModifier | null): EnemyState {
    const definition = getEnemy(kind);
    const position = this.spawnPosition(kind);
    const waveScale =
      kind === 'devourer' || kind === 'warden' ? 1 : 1 + Math.max(0, this.state.wave - 1) * 0.17;
    let health = definition.health * waveScale;
    let speed = definition.speed * (1 + Math.max(0, this.state.wave - 1) * 0.012);
    let damage = definition.damage * (1 + Math.max(0, this.state.wave - 1) * 0.05);
    if (eliteModifier) {
      health *= 2.5;
      damage *= 1.2;
      if (eliteModifier === 'fast') speed *= 1.55;
    }

    const enemy = this.enemyPool.acquire();
    Object.assign(enemy, {
      id: this.nextEnemyId++,
      kind,
      x: position.x,
      y: position.y,
      radius: definition.radius,
      health,
      maxHealth: health,
      shield: 0,
      speed,
      damage,
      rewardCash: definition.rewardCash * (eliteModifier ? 5 : 1),
      rewardXP: definition.rewardXP * (eliteModifier ? 4 : 1),
      attackCooldown: this.rng.between(0.2, definition.attackInterval),
      abilityCooldown: 1,
      hitFlash: 0,
      eliteModifier,
      status: {
        chillStacks: 0,
        chilledFor: 0,
        frozenFor: 0,
        freezeImmunityFor: 0,
        staticStacks: 0,
        frostbiteFor: 0,
      },
      bossPhase: kind === 'devourer' || kind === 'warden' ? 1 : 0,
      bossShielded: false,
    } satisfies EnemyState);
    this.state.enemies.push(enemy);
    this.emit('enemySpawned', { enemyId: enemy.id, kind });
    if (kind === 'warden' || kind === 'devourer')
      this.emit('bossEntered', { enemyId: enemy.id, kind, name: definition.name });
    return enemy;
  }

  private spawnPosition(kind: EnemyKind): Vec2 {
    const player = this.state.player;
    const angle = this.rng.between(0, Math.PI * 2);
    const isBoss = kind === 'warden' || kind === 'devourer';
    const distance = isBoss
      ? Math.min(this.state.bossArena.radius * 0.72, 235)
      : this.rng.between(285, 390);
    const originX = this.state.bossArena.active ? this.state.bossArena.centerX : player.x;
    const originY = this.state.bossArena.active ? this.state.bossArena.centerY : player.y;
    return {
      x: originX + Math.cos(angle) * distance,
      y: originY + Math.sin(angle) * distance,
    };
  }

  private updateCoreAbility(): void {
    if (this.state.coreAbilityCooldown > 0) return;
    const definition = getCore(this.state.coreId);
    const { ability } = definition;
    const masteryPower = 1 + this.meta.coreMasteryLevel * 0.12;

    if (ability.type === 'barrier') {
      this.state.core.barrier = Math.min(
        this.state.core.maxBarrier,
        this.state.core.barrier + ability.power * masteryPower,
      );
    }

    if (ability.type === 'chain') {
      const visited = new Set<number>();
      let origin = { x: this.state.player.x, y: this.state.player.y };
      for (let index = 0; index < ability.targets; index += 1) {
        const target = this.nearestEnemy(origin.x, origin.y, ability.radius, visited);
        if (!target) break;
        visited.add(target.id);
        this.damageEnemy(
          target,
          ability.power * masteryPower * this.state.core.damageMultiplier * Math.pow(0.82, index),
          'tesla',
        );
        origin = { x: target.x, y: target.y };
      }
    }

    if (ability.type === 'freeze') {
      for (const enemy of this.spatialHash.queryCircle(
        this.state.player.x,
        this.state.player.y,
        ability.radius,
      )) {
        if (this.deadEnemyIds.has(enemy.id)) continue;
        enemy.status.chilledFor = Math.max(enemy.status.chilledFor, 4);
        enemy.status.frozenFor = Math.max(
          enemy.status.frozenFor,
          1.45 + this.meta.coreMasteryLevel * 0.1,
        );
        enemy.status.freezeImmunityFor = Math.max(enemy.status.freezeImmunityFor, 2.8);
        this.damageEnemy(
          enemy,
          ability.power * masteryPower * this.state.core.damageMultiplier,
          'cryo',
        );
      }
    }

    if (ability.type === 'singularity') {
      for (const enemy of this.spatialHash.queryCircle(
        this.state.player.x,
        this.state.player.y,
        ability.radius,
      )) {
        if (this.deadEnemyIds.has(enemy.id)) continue;
        const direction = this.directionTo(
          enemy.x,
          enemy.y,
          this.state.player.x,
          this.state.player.y,
        );
        const distance = Math.hypot(enemy.x - this.state.player.x, enemy.y - this.state.player.y);
        const pull = Math.min(38, 12 + distance * 0.11);
        enemy.x += direction.x * pull;
        enemy.y += direction.y * pull;
        this.damageEnemy(
          enemy,
          ability.power * masteryPower * this.state.core.damageMultiplier,
          'utility',
        );
      }
    }

    this.state.coreAbilityCooldown =
      definition.abilityCooldown * (1 - Math.min(0.5, this.state.core.cooldownReduction));
    this.emit('coreAbilityActivated', {
      coreId: definition.id,
      name: definition.abilityName,
      color: definition.color,
      radius: ability.radius,
    });
  }

  private updateStatusEffects(): void {
    const frostbiteRank = this.rank('frostbite');
    for (const enemy of this.state.enemies) {
      enemy.hitFlash = Math.max(0, enemy.hitFlash - FIXED_STEP * 5);
      enemy.status.chilledFor = Math.max(0, enemy.status.chilledFor - FIXED_STEP);
      enemy.status.frozenFor = Math.max(0, enemy.status.frozenFor - FIXED_STEP);
      enemy.status.freezeImmunityFor = Math.max(0, enemy.status.freezeImmunityFor - FIXED_STEP);
      enemy.status.frostbiteFor = Math.max(0, enemy.status.frostbiteFor - FIXED_STEP);
      if (enemy.status.chilledFor === 0) enemy.status.chillStacks = 0;
      if (enemy.status.frostbiteFor > 0 && frostbiteRank > 0) {
        const damage = (2.2 + frostbiteRank * 1.5) * FIXED_STEP * this.state.core.damageMultiplier;
        enemy.health -= damage;
        this.state.stats.damageDealt += damage;
        if (enemy.health <= 0) this.killEnemy(enemy, 'cryo');
      }
      if (enemy.eliteModifier === 'regenerating' && enemy.health > 0) {
        enemy.health = Math.min(
          enemy.maxHealth,
          enemy.health + enemy.maxHealth * 0.012 * FIXED_STEP,
        );
      }
    }
  }

  private updateEnemies(): void {
    const huntProtocol = this.isHuntProtocolActive();
    for (const enemy of [...this.state.enemies]) {
      if (this.deadEnemyIds.has(enemy.id)) continue;
      if (enemy.kind === 'devourer') this.updateBoss(enemy);
      if (enemy.kind === 'warden') this.updateWarden(enemy);
      if (enemy.kind === 'protector') this.updateProtector(enemy);

      const deltaX = this.state.player.x - enemy.x;
      const deltaY = this.state.player.y - enemy.y;
      const distance = Math.hypot(deltaX, deltaY) || 1;
      const definition = getEnemy(enemy.kind);
      const attackRange = huntProtocol
        ? CORE_RADIUS + enemy.radius + 3
        : definition.attackRange + CORE_RADIUS;
      const frozen = enemy.status.frozenFor > 0;
      const slowFactor =
        enemy.status.chilledFor > 0 ? Math.max(0.38, 1 - enemy.status.chillStacks * 0.11) : 1;
      const bossEnrage =
        (enemy.kind === 'devourer' && enemy.bossPhase === 4) ||
        (enemy.kind === 'warden' && enemy.bossPhase === 2)
          ? 1.4
          : 1;
      const isBoss = enemy.kind === 'warden' || enemy.kind === 'devourer';
      const pursuitSpeed = isBoss
        ? enemy.speed
        : calculatePursuitSpeed(enemy.speed, this.state.player.speed, distance, huntProtocol);
      const movementSpeed = calculateEnemyMovementSpeed(
        pursuitSpeed,
        this.state.player.speed,
        slowFactor,
        bossEnrage,
        huntProtocol,
      );

      if (!frozen && distance > attackRange) {
        const seekX = deltaX / distance;
        const seekY = deltaY / distance;
        let avoidanceX = 0;
        let avoidanceY = 0;
        let separationPressure = 0;
        for (const nearby of this.spatialHash.queryCircle(enemy.x, enemy.y, enemy.radius * 2.8)) {
          if (nearby.id === enemy.id || this.deadEnemyIds.has(nearby.id)) continue;
          const awayX = enemy.x - nearby.x;
          const awayY = enemy.y - nearby.y;
          const awayDistance = Math.hypot(awayX, awayY) || 1;
          const desiredGap = (enemy.radius + nearby.radius) * 1.35;
          if (awayDistance >= desiredGap) continue;
          const pressure = (desiredGap - awayDistance) / desiredGap;
          avoidanceX += (awayX / awayDistance) * pressure;
          avoidanceY += (awayY / awayDistance) * pressure;
          separationPressure += pressure;
        }
        const avoidanceLength = Math.hypot(avoidanceX, avoidanceY);
        const avoidanceWeight = Math.min(0.24, separationPressure * 0.055);
        let moveX = seekX;
        let moveY = seekY;
        if (avoidanceLength > 0.001 && avoidanceWeight > 0) {
          moveX = seekX * (1 - avoidanceWeight) + (avoidanceX / avoidanceLength) * avoidanceWeight;
          moveY = seekY * (1 - avoidanceWeight) + (avoidanceY / avoidanceLength) * avoidanceWeight;
        }
        const moveLength = Math.hypot(moveX, moveY) || 1;
        let directionX = moveX / moveLength;
        let directionY = moveY / moveLength;
        const forwardProgress = directionX * seekX + directionY * seekY;
        if (forwardProgress < 0.82) {
          directionX = seekX;
          directionY = seekY;
        }
        enemy.x += directionX * movementSpeed * FIXED_STEP;
        enemy.y += directionY * movementSpeed * FIXED_STEP;
      } else if (!frozen) {
        enemy.attackCooldown -= FIXED_STEP;
        if (enemy.attackCooldown <= 0) {
          enemy.attackCooldown = definition.attackInterval / bossEnrage;
          if (enemy.kind === 'ranged') this.fireEnemyProjectile(enemy);
          else this.damageCore(enemy.damage);
        }
        if (enemy.kind === 'ranged' && distance > CORE_RADIUS + enemy.radius + 6) {
          enemy.x += (deltaX / distance) * movementSpeed * 0.34 * FIXED_STEP;
          enemy.y += (deltaY / distance) * movementSpeed * 0.34 * FIXED_STEP;
        }
      }
    }
  }

  private isHuntProtocolActive(): boolean {
    return (
      this.state.waveSpawnFinished &&
      this.state.enemies.length > 0 &&
      this.state.enemies.length <= 5 &&
      !this.state.enemies.some((enemy) => enemy.kind === 'warden' || enemy.kind === 'devourer')
    );
  }

  private updateProtector(protector: EnemyState): void {
    protector.abilityCooldown -= FIXED_STEP;
    if (protector.abilityCooldown > 0) return;
    protector.abilityCooldown = 3.6;
    const nearby = this.spatialHash.queryCircle(protector.x, protector.y, 78);
    for (const enemy of nearby) {
      if (enemy.id !== protector.id && !this.deadEnemyIds.has(enemy.id)) {
        enemy.shield = Math.min(enemy.maxHealth * 0.28, enemy.shield + enemy.maxHealth * 0.14);
      }
    }
  }

  private updateWarden(warden: EnemyState): void {
    const phase = warden.health / warden.maxHealth > 0.5 ? 1 : 2;
    if (phase !== warden.bossPhase) {
      warden.bossPhase = phase;
      warden.abilityCooldown = 0;
      this.emit('bossPhase', { phase, kind: 'warden' });
    }
    warden.abilityCooldown -= FIXED_STEP;
    if (warden.abilityCooldown > 0) return;
    warden.abilityCooldown = phase === 1 ? 4.2 : 2.8;
    if (phase === 1) {
      warden.bossShielded = !warden.bossShielded;
      for (const enemy of this.spatialHash.queryCircle(warden.x, warden.y, 110)) {
        if (enemy.id !== warden.id)
          enemy.shield = Math.min(enemy.maxHealth * 0.32, enemy.shield + enemy.maxHealth * 0.18);
      }
    } else {
      warden.bossShielded = false;
      this.damageCore(7);
      if (this.state.enemies.length < MAX_ENEMIES - 2) this.spawnEnemy('fast', null);
    }
  }

  private updateBoss(boss: EnemyState): void {
    const healthRatio = boss.health / boss.maxHealth;
    const phase = healthRatio > 0.75 ? 1 : healthRatio > 0.5 ? 2 : healthRatio > 0.25 ? 3 : 4;
    if (phase !== boss.bossPhase) {
      boss.bossPhase = phase;
      boss.abilityCooldown = 0;
      this.emit('bossPhase', { phase, kind: 'devourer' });
    }

    if (phase === 1 || phase === 4) {
      this.bossSummonCooldown -= FIXED_STEP;
      if (this.bossSummonCooldown <= 0 && this.state.enemies.length < MAX_ENEMIES - 5) {
        this.bossSummonCooldown = phase === 4 ? 3.2 : 5;
        const count = phase === 4 ? 4 : 2;
        for (let index = 0; index < count; index += 1)
          this.spawnEnemy(index % 2 === 0 ? 'fast' : 'basic', null);
      }
    }
    if (phase === 2) {
      boss.abilityCooldown -= FIXED_STEP;
      if (boss.abilityCooldown <= 0) {
        boss.bossShielded = !boss.bossShielded;
        boss.abilityCooldown = boss.bossShielded ? 2 : 3.5;
      }
    } else {
      boss.bossShielded = false;
    }
    if (phase === 3 || phase === 4) {
      this.bossDisruptionCooldown -= FIXED_STEP;
      if (this.bossDisruptionCooldown <= 0) {
        this.bossDisruptionCooldown = phase === 4 ? 3.5 : 5.5;
        this.damageCore(phase === 4 ? 13 : 8);
      }
    }
  }

  private updateWeapons(): void {
    const kinetic = this.state.weapons.find((weapon) => weapon.family === 'kinetic')!;
    const tesla = this.state.weapons.find((weapon) => weapon.family === 'tesla')!;
    const cryo = this.state.weapons.find((weapon) => weapon.family === 'cryo')!;
    const overdrive = this.state.overdriveFor > 0 ? 1.38 : 1;

    kinetic.cooldown -= FIXED_STEP;
    if (kinetic.cooldown <= 0) {
      const target = this.nearestEnemy(this.state.player.x, this.state.player.y, 330);
      if (target) {
        this.fireKinetic(target);
        const gatling = 1 + this.rank('gatling_mode') * 0.14;
        kinetic.cooldown = 0.5 / (this.state.core.attackSpeedMultiplier * gatling * overdrive);
      }
    }

    tesla.enabled = this.rank('tesla_arc') > 0;
    tesla.visualLevel = tesla.enabled ? 1 + this.rank('chain_lightning') : 0;
    if (tesla.enabled) {
      tesla.cooldown -= FIXED_STEP;
      if (tesla.cooldown <= 0) {
        this.fireTesla();
        tesla.cooldown = 0.95 / (this.state.core.attackSpeedMultiplier * overdrive);
      }
    }

    cryo.enabled = this.rank('cryo_emitter') > 0;
    cryo.visualLevel = cryo.enabled ? 1 + this.rank('cryo_field') : 0;
    if (cryo.enabled) {
      cryo.cooldown -= FIXED_STEP;
      if (cryo.cooldown <= 0) {
        this.fireCryo();
        cryo.cooldown =
          (2.25 * (1 - Math.min(0.55, this.state.core.cooldownReduction))) / overdrive;
      }
    }

    if (this.rank('gravity_well') > 0) {
      this.gravityCooldown -= FIXED_STEP;
      if (this.gravityCooldown <= 0) {
        this.fireGravity();
        this.gravityCooldown = 3.8 * (1 - Math.min(0.55, this.state.core.cooldownReduction));
      }
    }
  }

  private fireKinetic(target: EnemyState): void {
    if (this.state.projectiles.length >= MAX_PROJECTILES) return;
    const direction = this.directionTo(
      this.state.player.x,
      this.state.player.y,
      target.x,
      target.y,
    );
    const projectile = this.projectilePool.acquire();
    Object.assign(projectile, {
      id: this.nextProjectileId++,
      source: 'player',
      family: 'kinetic',
      x: this.state.player.x,
      y: this.state.player.y,
      previousX: this.state.player.x,
      previousY: this.state.player.y,
      velocityX: direction.x * PLAYER_PROJECTILE_SPEED,
      velocityY: direction.y * PLAYER_PROJECTILE_SPEED,
      radius: 4 + this.rank('heavy_rounds') * 0.5,
      damage: 28 * (1 + this.rank('heavy_rounds') * 0.22) * this.state.core.damageMultiplier,
      ttl: 1.4,
      pierce: this.rank('piercing_ammo'),
      bounces: this.rank('ricochet'),
      explosionRadius: this.rank('explosive_shells') * 11 * this.state.core.areaMultiplier,
      hitIds: [],
    } satisfies ProjectileState);
    this.state.projectiles.push(projectile);
    this.emit('projectileFired', { projectileId: projectile.id, family: 'kinetic' });
  }

  private fireTesla(): void {
    const firstTarget = this.nearestEnemy(this.state.player.x, this.state.player.y, 240);
    if (!firstTarget) return;
    const chainBonus = this.state.evolutionId === 'tesla_singularity' ? 3 : 0;
    const maxTargets = 1 + this.rank('chain_lightning') + chainBonus;
    const visited = new Set<number>();
    const points: Vec2[] = [{ x: this.state.player.x, y: this.state.player.y }];
    let target: EnemyState | null = firstTarget;
    let damage = 18 * this.state.core.damageMultiplier;
    if (this.state.evolutionId === 'tesla_singularity') damage *= 1.8;
    const retention = Math.min(0.96, 0.68 + this.rank('conductive_target') * 0.1);

    for (let index = 0; index < maxTargets && target; index += 1) {
      visited.add(target.id);
      points.push({ x: target.x, y: target.y });
      let appliedDamage = damage * Math.pow(retention, index);
      if (this.state.synergies.includes('superconductor') && target.status.chilledFor > 0)
        appliedDamage *= 1.45;
      target.status.staticStacks = Math.min(8, target.status.staticStacks + 1);
      appliedDamage *= 1 + target.status.staticStacks * this.rank('static_charge') * 0.04;
      this.damageEnemy(target, appliedDamage, 'tesla');
      target = this.nearestEnemy(points.at(-1)!.x, points.at(-1)!.y, 105, visited);
    }

    this.teslaVolley += 1;
    if (this.rank('overcharge') > 0 && this.teslaVolley % 4 === 0) {
      const radius = 90 + this.rank('overcharge') * 12;
      for (const enemy of this.spatialHash.queryCircle(firstTarget.x, firstTarget.y, radius)) {
        if (!visited.has(enemy.id))
          this.damageEnemy(
            enemy,
            9 * this.rank('overcharge') * this.state.core.damageMultiplier,
            'tesla',
          );
      }
    }
    this.emit('teslaFired', { points });
  }

  private fireCryo(): void {
    const radius = 104 * (1 + this.rank('cryo_field') * 0.18) * this.state.core.areaMultiplier;
    const freezeThreshold = Math.max(2, 6 - this.rank('deep_freeze'));
    for (const enemy of this.spatialHash.queryCircle(
      this.state.player.x,
      this.state.player.y,
      radius,
    )) {
      const distance = Math.hypot(enemy.x - this.state.player.x, enemy.y - this.state.player.y);
      if (distance > radius + enemy.radius || this.deadEnemyIds.has(enemy.id)) continue;
      enemy.status.chillStacks = Math.min(8, enemy.status.chillStacks + 1);
      enemy.status.chilledFor = 3.2;
      enemy.status.frostbiteFor = this.rank('frostbite') > 0 ? 3 : enemy.status.frostbiteFor;
      if (enemy.status.chillStacks >= freezeThreshold && enemy.status.freezeImmunityFor <= 0) {
        enemy.status.frozenFor = 1.2 + this.rank('deep_freeze') * 0.18;
        enemy.status.freezeImmunityFor = 3.2;
        enemy.status.chillStacks = 0;
      }
      this.damageEnemy(
        enemy,
        (4 + this.rank('frostbite') * 2) * this.state.core.damageMultiplier,
        'cryo',
      );
    }
    this.emit('cryoPulse', { radius });
  }

  private fireGravity(): void {
    const rank = this.rank('gravity_well');
    const radius = (145 + rank * 14) * this.state.core.areaMultiplier;
    const enemies = this.spatialHash.queryCircle(this.state.player.x, this.state.player.y, radius);
    for (const enemy of enemies) {
      if (this.deadEnemyIds.has(enemy.id)) continue;
      const distance = Math.hypot(enemy.x - this.state.player.x, enemy.y - this.state.player.y);
      if (distance > radius) continue;
      const direction = this.directionTo(
        enemy.x,
        enemy.y,
        this.state.player.x,
        this.state.player.y,
      );
      const pull = 10 + rank * 5;
      enemy.x += direction.x * pull;
      enemy.y += direction.y * pull;
      this.damageEnemy(enemy, rank * 4 * this.state.core.damageMultiplier, 'utility', false);
      if (this.state.synergies.includes('cryo_vortex')) {
        enemy.status.chilledFor = 3;
        enemy.status.chillStacks = Math.min(8, enemy.status.chillStacks + 2);
      }
      if (this.state.synergies.includes('thunderstorm')) {
        this.damageEnemy(enemy, 8 * this.state.core.damageMultiplier, 'tesla');
      }
    }
    this.emit('gravityPulse', { radius });
  }

  private updateProjectiles(): void {
    this.spatialHash.rebuild(
      this.state.enemies.filter((enemy) => !this.deadEnemyIds.has(enemy.id)),
    );
    for (const projectile of this.state.projectiles) {
      projectile.previousX = projectile.x;
      projectile.previousY = projectile.y;
      projectile.x += projectile.velocityX * FIXED_STEP;
      projectile.y += projectile.velocityY * FIXED_STEP;
      projectile.ttl -= FIXED_STEP;

      if (projectile.source === 'enemy') {
        if (
          Math.hypot(projectile.x - this.state.player.x, projectile.y - this.state.player.y) <=
          CORE_RADIUS + projectile.radius
        ) {
          this.damageCore(projectile.damage);
          projectile.ttl = 0;
        }
        continue;
      }

      const candidates = this.spatialHash.queryCircle(projectile.x, projectile.y, 24);
      for (const enemy of candidates) {
        if (projectile.hitIds.includes(enemy.id) || this.deadEnemyIds.has(enemy.id)) continue;
        if (
          Math.hypot(projectile.x - enemy.x, projectile.y - enemy.y) >
          projectile.radius + enemy.radius
        )
          continue;
        projectile.hitIds.push(enemy.id);
        let damage = projectile.damage;
        if (this.state.evolutionId === 'shatter_cannon') damage *= 1.75;
        if (enemy.status.frozenFor > 0 && this.state.synergies.includes('shatter_rounds'))
          damage *= 1.55;
        this.damageEnemy(enemy, damage, 'kinetic');
        if (projectile.explosionRadius > 0)
          this.explode(
            projectile.x,
            projectile.y,
            projectile.explosionRadius,
            damage * 0.42,
            enemy.id,
          );

        if (projectile.pierce > 0) {
          projectile.pierce -= 1;
        } else if (projectile.bounces > 0) {
          const next = this.nearestEnemy(
            projectile.x,
            projectile.y,
            130,
            new Set(projectile.hitIds),
          );
          if (next) {
            const direction = this.directionTo(projectile.x, projectile.y, next.x, next.y);
            projectile.velocityX = direction.x * PLAYER_PROJECTILE_SPEED;
            projectile.velocityY = direction.y * PLAYER_PROJECTILE_SPEED;
            projectile.bounces -= 1;
          } else projectile.ttl = 0;
        } else projectile.ttl = 0;
        break;
      }
    }
  }

  private fireEnemyProjectile(enemy: EnemyState): void {
    if (this.state.projectiles.length >= MAX_PROJECTILES) return;
    const direction = this.directionTo(enemy.x, enemy.y, this.state.player.x, this.state.player.y);
    const projectile = this.projectilePool.acquire();
    Object.assign(projectile, {
      id: this.nextProjectileId++,
      source: 'enemy',
      family: 'enemy',
      x: enemy.x,
      y: enemy.y,
      previousX: enemy.x,
      previousY: enemy.y,
      velocityX: direction.x * ENEMY_PROJECTILE_SPEED,
      velocityY: direction.y * ENEMY_PROJECTILE_SPEED,
      radius: 4,
      damage: enemy.damage,
      ttl: 2.5,
      pierce: 0,
      bounces: 0,
      explosionRadius: 0,
      hitIds: [],
    } satisfies ProjectileState);
    this.state.projectiles.push(projectile);
    this.emit('projectileFired', { projectileId: projectile.id, family: 'enemy' });
  }

  private damageEnemy(
    enemy: EnemyState,
    rawDamage: number,
    family: WeaponFamily,
    canCrit = true,
  ): void {
    if (this.deadEnemyIds.has(enemy.id)) return;
    const critical = canCrit && this.rng.next() < this.state.core.critChance;
    let damage = rawDamage * (critical ? this.state.core.critMultiplier : 1);
    if (enemy.eliteModifier === 'armored') damage *= 100 / 180;
    if (enemy.bossShielded) damage *= 0.28;
    if (enemy.shield > 0) {
      const absorbed = Math.min(enemy.shield, damage);
      enemy.shield -= absorbed;
      damage -= absorbed;
    }
    if (damage <= 0) return;
    enemy.health -= damage;
    enemy.hitFlash = 1;
    this.state.stats.damageDealt += damage;
    this.emit('enemyDamaged', { enemyId: enemy.id, amount: damage, critical, family });
    if (enemy.health <= 0) this.killEnemy(enemy, family);
  }

  private killEnemy(enemy: EnemyState, family: WeaponFamily): void {
    if (this.deadEnemyIds.has(enemy.id)) return;
    this.deadEnemyIds.add(enemy.id);
    this.state.stats.kills += 1;
    this.state.combo += 1;
    this.state.comboTimer = 2.65;
    this.state.stats.maxCombo = Math.max(this.state.stats.maxCombo, this.state.combo);
    const comboCashMultiplier = 1 + Math.min(0.5, this.state.combo * 0.012);
    this.state.cash += Math.round(
      enemy.rewardCash * this.state.core.cashMultiplier * comboCashMultiplier,
    );
    this.state.momentum +=
      enemy.kind === 'warden' || enemy.kind === 'devourer' ? 32 : enemy.eliteModifier ? 14 : 4;
    if (this.state.momentum >= 100) {
      this.state.momentum = 0;
      this.state.overdriveFor = 6;
      this.emit('overdriveActivated', { duration: 6 });
    }
    this.spawnXpPickup(
      enemy.x,
      enemy.y,
      enemy.rewardXP * this.state.core.xpMultiplier,
      enemy.kind,
      enemy.eliteModifier !== null,
    );
    this.emit('enemyKilled', { enemyId: enemy.id, kind: enemy.kind, x: enemy.x, y: enemy.y });

    if ((enemy.kind === 'warden' || enemy.kind === 'devourer') && this.state.bossArena.active) {
      const kind = enemy.kind;
      this.state.bossArena.active = false;
      this.state.bossArena.kind = null;
      this.emit('bossArenaReleased', { kind });
    }

    if (enemy.status.frozenFor > 0 && this.rank('shatter') > 0) {
      const radius = 34 + this.rank('shatter') * 8;
      const damage = enemy.maxHealth * (0.08 + this.rank('shatter') * 0.04);
      this.explode(enemy.x, enemy.y, radius, damage, enemy.id);
    }
    if (family === 'kinetic' && this.state.synergies.includes('implosion_rounds')) {
      this.explode(enemy.x, enemy.y, 45, 13 * this.state.core.damageMultiplier, enemy.id);
    }
  }

  private explode(x: number, y: number, radius: number, damage: number, excludedId: number): void {
    for (const enemy of this.spatialHash.queryCircle(x, y, radius)) {
      if (enemy.id === excludedId || this.deadEnemyIds.has(enemy.id)) continue;
      if (Math.hypot(enemy.x - x, enemy.y - y) <= radius + enemy.radius) {
        this.damageEnemy(enemy, damage, 'kinetic', false);
      }
    }
  }

  private damageCore(rawDamage: number): void {
    if (this.state.phase !== 'combat') return;
    let damage = mitigateDamage(rawDamage, this.state.core.armor);
    if (this.state.core.barrier > 0) {
      const absorbed = Math.min(this.state.core.barrier, damage);
      this.state.core.barrier -= absorbed;
      damage -= absorbed;
    }
    this.state.core.health = Math.max(0, this.state.core.health - damage);
    this.state.stats.damageTaken += damage;
    this.emit('coreDamaged', { amount: damage, health: this.state.core.health });
    if (this.state.core.health <= 0) this.endRun('defeat');
  }

  private spawnXpPickup(
    x: number,
    y: number,
    value: number,
    enemyKind: EnemyKind,
    elite: boolean,
  ): void {
    const adjustedValue = Math.max(1, Math.round(value));
    const forcedTier: XpTier | null =
      enemyKind === 'devourer' ? 'prismatic' : enemyKind === 'warden' || elite ? 'gold' : null;
    const tier = forcedTier ?? this.xpTierForValue(adjustedValue);

    if (this.state.xpPickups.length >= MAX_XP_PICKUPS) {
      const existing = this.state.xpPickups[this.rng.integer(0, this.state.xpPickups.length - 1)];
      if (existing) {
        existing.value += adjustedValue;
        existing.tier = this.xpTierForValue(existing.value);
        existing.radius = this.xpRadius(existing.tier);
      }
      return;
    }

    const spreadAngle = this.rng.between(0, Math.PI * 2);
    const spread = this.rng.between(
      3,
      enemyKind === 'warden' || enemyKind === 'devourer' ? 24 : 12,
    );
    const pickup = this.xpPickupPool.acquire();
    Object.assign(pickup, {
      id: this.nextXpPickupId++,
      x: x + Math.cos(spreadAngle) * spread,
      y: y + Math.sin(spreadAngle) * spread,
      previousX: x,
      previousY: y,
      value: adjustedValue,
      tier,
      radius: this.xpRadius(tier),
      magnetized: false,
      age: 0,
    } satisfies XpPickupState);
    this.state.xpPickups.push(pickup);
    this.emit('xpDropped', {
      pickupId: pickup.id,
      tier,
      value: adjustedValue,
      x: pickup.x,
      y: pickup.y,
    });
  }

  private updateXpPickups(): void {
    const collectedIds = new Set<number>();
    const player = this.state.player;
    const recallAll = this.state.waveSpawnFinished && this.state.enemies.length === 0;
    for (const pickup of this.state.xpPickups) {
      pickup.previousX = pickup.x;
      pickup.previousY = pickup.y;
      pickup.age += FIXED_STEP;
      const deltaX = player.x - pickup.x;
      const deltaY = player.y - pickup.y;
      const distance = Math.hypot(deltaX, deltaY) || 1;
      const tierPull = pickup.tier === 'prismatic' ? 1.22 : pickup.tier === 'gold' ? 1.12 : 1;
      const magnetRadius = recallAll
        ? Number.POSITIVE_INFINITY
        : this.state.core.pickupRadius * tierPull;
      pickup.magnetized = recallAll || distance <= magnetRadius;
      if (pickup.magnetized) {
        const pullSpeed = recallAll
          ? 620 + distance * 1.4
          : 100 + (1 - Math.min(1, distance / magnetRadius)) * 430;
        const movement = Math.min(distance, pullSpeed * FIXED_STEP);
        pickup.x += (deltaX / distance) * movement;
        pickup.y += (deltaY / distance) * movement;
      }
      if (distance <= CORE_RADIUS + pickup.radius + 9) {
        collectedIds.add(pickup.id);
        this.grantXp(pickup.value);
        this.emit('xpCollected', {
          pickupId: pickup.id,
          tier: pickup.tier,
          value: pickup.value,
          x: pickup.x,
          y: pickup.y,
        });
      }
    }
    if (collectedIds.size === 0) return;
    this.state.xpPickups = this.state.xpPickups.filter((pickup) => {
      if (!collectedIds.has(pickup.id)) return true;
      this.xpPickupPool.release(pickup);
      return false;
    });
  }

  private xpTierForValue(value: number): XpTier {
    if (value <= 4) return 'gray';
    if (value <= 10) return 'blue';
    if (value <= 50) return 'gold';
    return 'prismatic';
  }

  private xpRadius(tier: XpTier): number {
    if (tier === 'gray') return 3.5;
    if (tier === 'blue') return 5;
    if (tier === 'gold') return 7;
    return 9.5;
  }

  private grantXp(amount: number): void {
    this.state.xp += amount;
    while (this.state.xp >= this.state.xpToNext) {
      this.state.xp -= this.state.xpToNext;
      this.state.level += 1;
      this.state.pendingLevelUps += 1;
      this.state.xpToNext = xpRequiredForLevel(this.state.level);
      this.emit('levelUp', { level: this.state.level });
    }
    if (this.state.pendingLevelUps > 0 && this.state.phase === 'combat') this.openLevelUp();
  }

  private openLevelUp(): void {
    this.state.phase = 'level-up';
    this.state.upgradeOffers = generateUpgradeOffers(this.state, this.meta, this.rng);
    if (this.state.upgradeOffers.length === 0) {
      this.state.pendingLevelUps = 0;
      this.state.phase = 'combat';
    }
  }

  private selectUpgrade(upgradeId: string, expectedRank: number): void {
    const offer = this.state.upgradeOffers.find((candidate) => candidate.id === upgradeId);
    if (!offer) return;
    const definition = getUpgrade(upgradeId);
    const currentRank = this.rank(upgradeId);
    if (
      currentRank >= definition.maxRank ||
      offer.currentRank !== currentRank ||
      offer.nextRank !== expectedRank ||
      expectedRank !== currentRank + 1 ||
      expectedRank > definition.maxRank
    )
      return;
    this.state.upgradeRanks[upgradeId] = currentRank + 1;
    this.state.pendingLevelUps = Math.max(0, this.state.pendingLevelUps - 1);
    this.state.upgradeOffers = [];
    recalculateCoreStats(this.state, this.meta);
    this.emit('systemUpgradeActivated', {
      upgradeId,
      name: definition.name,
      rank: currentRank + 1,
      maxRank: definition.maxRank,
      tier: definition.tier,
      rarity: definition.rarity,
      family: definition.family,
    });

    const unlocked = newlyUnlockedSynergies(this.state);
    for (const synergy of unlocked) {
      this.state.synergies.push(synergy.id);
      this.emit('synergyUnlocked', { synergyId: synergy.id, name: synergy.name });
    }

    const evolution = eligibleEvolution(this.state);
    if (evolution) {
      this.pendingPhaseAfterEvolution = 'combat';
      this.triggerEvolution(evolution);
    } else if (this.state.pendingLevelUps > 0) {
      this.openLevelUp();
    } else {
      this.state.phase = 'combat';
    }
  }

  private triggerEvolution(evolution: EvolutionDefinition): void {
    this.state.evolutionId = evolution.id;
    this.state.pendingEvolutionId = evolution.id;
    this.state.evolutionTrigger = false;
    this.state.phase = 'evolution';
    this.emit('evolutionUnlocked', { evolutionId: evolution.id, name: evolution.name });
  }

  private completeWave(): void {
    if (this.state.phase !== 'combat') return;
    this.emit('waveCompleted', { wave: this.state.wave });
    if (this.state.wave === 5) this.state.evolutionTrigger = true;
    if (this.state.wave >= WAVES.length) {
      this.endRun('victory');
      return;
    }

    const evolution = eligibleEvolution(this.state);
    if (evolution) {
      this.pendingPhaseAfterEvolution = 'intermission';
      this.state.shopOffers = generateShopOffers(this.state, this.rng);
      this.triggerEvolution(evolution);
      return;
    }

    this.state.phase = 'intermission';
    this.state.shopOffers = generateShopOffers(this.state, this.rng);
  }

  private endRun(result: 'victory' | 'defeat'): void {
    if (this.state.phase === 'victory' || this.state.phase === 'defeat') return;
    const completedWaves = result === 'victory' ? 10 : Math.max(0, this.state.wave - 1);
    const reward = 20 + completedWaves * 18 + (result === 'victory' ? 120 : 0);
    this.state.resultReward = reward;
    this.state.resultProfileXp = 24 + completedWaves * 13 + (result === 'victory' ? 90 : 0);
    this.state.resultCoreXp = 20 + completedWaves * 11 + (result === 'victory' ? 75 : 0);
    this.state.blueprintReward =
      result === 'victory' && !this.meta.unlockedBlueprints.includes('gravity_matrix')
        ? 'gravity_matrix'
        : null;
    this.state.phase = result;
    this.emit('runEnded', { result, reward });
  }

  private cleanupEntities(): void {
    this.state.enemies = this.state.enemies.filter((enemy) => {
      if (!this.deadEnemyIds.has(enemy.id)) return true;
      this.deadEnemyIds.delete(enemy.id);
      this.enemyPool.release(enemy);
      return false;
    });
    this.state.projectiles = this.state.projectiles.filter((projectile) => {
      const active =
        projectile.ttl > 0 &&
        Math.abs(projectile.x - this.state.player.x) < 760 &&
        Math.abs(projectile.y - this.state.player.y) < 960;
      if (!active) this.projectilePool.release(projectile);
      return active;
    });
  }

  private nearestEnemy(
    x: number,
    y: number,
    range: number,
    excluded: ReadonlySet<number> = new Set(),
  ): EnemyState | null {
    let nearest: EnemyState | null = null;
    let nearestDistance = range;
    for (const enemy of this.spatialHash.queryCircle(x, y, range)) {
      if (excluded.has(enemy.id) || this.deadEnemyIds.has(enemy.id)) continue;
      const distance = Math.hypot(enemy.x - x, enemy.y - y);
      if (distance < nearestDistance) {
        nearest = enemy;
        nearestDistance = distance;
      }
    }
    return nearest;
  }

  private directionTo(fromX: number, fromY: number, toX: number, toY: number): Vec2 {
    const deltaX = toX - fromX;
    const deltaY = toY - fromY;
    const length = Math.hypot(deltaX, deltaY) || 1;
    return { x: deltaX / length, y: deltaY / length };
  }

  private rank(upgradeId: string): number {
    return this.state.upgradeRanks[upgradeId] ?? 0;
  }

  private emit<K extends keyof GameEventMap>(type: K, payload: GameEventMap[K]): void {
    this.eventQueue.push(this.events.emit(type, payload));
  }
}

export function getUpgradeById(id: string): UpgradeOffer | null {
  const definition = UPGRADES.find((upgrade) => upgrade.id === id);
  if (!definition) return null;
  return {
    id: definition.id,
    name: definition.name,
    description: definition.description,
    family: definition.family,
    rarity: definition.rarity,
    tier: definition.tier,
    maxRank: definition.maxRank,
    path:
      definition.requirements.length === 0
        ? 'ROOT SYSTEM'
        : `FROM ${definition.requirements.map((item) => getUpgrade(item.upgradeId).name).join(' + ')}`,
    currentRank: 0,
    nextRank: 1,
  };
}

export function getSynergyName(id: string): string {
  return SYNERGIES.find((synergy) => synergy.id === id)?.name ?? id;
}

export function getEvolutionName(id: string): string {
  return EVOLUTIONS.find((evolution) => evolution.id === id)?.name ?? id;
}
